import { useState, useRef } from "react";
import { api } from "@/lib/api";
import { Upload, FileText, Sparkles, AlertTriangle, CheckCircle2, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function Negotiate() {
  const [mode, setMode] = useState("paste");
  const [vehicle, setVehicle] = useState("");
  const [quoteText, setQuoteText] = useState("");
  const [filePath, setFilePath] = useState(null);
  const [fileName, setFileName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const uploadFile = async (f) => {
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("purpose", "negotiation_quote");
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFilePath(data.path);
      setFileName(f.name);
      toast.success("Uploaded. Now click Analyze.");
    } catch (e) {
      toast.error("Upload failed");
    } finally { setUploading(false); }
  };

  const analyze = async () => {
    if (mode === "paste" && !quoteText.trim()) { toast.error("Paste the quote text"); return; }
    if (mode === "upload" && !filePath) { toast.error("Upload a PDF first"); return; }
    setAnalyzing(true);
    setResult(null);
    try {
      const body = { vehicle };
      if (mode === "paste") body.quote_text = quoteText;
      else body.file_path = filePath;
      const { data } = await api.post("/negotiate", body);
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analysis failed");
    } finally { setAnalyzing(false); }
  };

  const scoreColor = result?.fair_deal_score >= 7 ? "text-emerald-400"
    : result?.fair_deal_score >= 5 ? "text-yellow-400" : "text-red-400";

  return (
    <div className="max-w-[1100px] mx-auto px-5 lg:px-10 py-12 md:py-16" data-testid="negotiate-page">
      <div className="flex items-center gap-2 mb-3"><Sparkles className="w-4 h-4 text-silver" /><span className="label-mono">AI Negotiation Assistant</span></div>
      <h1 className="font-display text-4xl md:text-6xl font-light tracking-tighter mb-3">Is this deal fair?</h1>
      <p className="text-gray-400 max-w-2xl mb-10">Paste a dealer quote — or upload the PDF worksheet — and we'll score it 0-10, extract the real numbers, and flag hidden add-ons.</p>

      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 glass rounded-2xl p-7">
          <Input placeholder="Vehicle (e.g. 2025 BMW X5 xDrive40i)" value={vehicle} onChange={(e) => setVehicle(e.target.value)} className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-xl text-white mb-5" data-testid="negotiate-vehicle" />

          <div className="flex gap-2 mb-5">
            <button onClick={() => setMode("paste")} className={`flex-1 py-2.5 rounded-full text-xs border ${mode === "paste" ? "silver-gradient text-[#050507] border-transparent" : "border-white/10"}`} data-testid="negotiate-mode-paste">Paste Text</button>
            <button onClick={() => setMode("upload")} className={`flex-1 py-2.5 rounded-full text-xs border ${mode === "upload" ? "silver-gradient text-[#050507] border-transparent" : "border-white/10"}`} data-testid="negotiate-mode-upload">Upload PDF</button>
          </div>

          {mode === "paste" ? (
            <textarea
              rows={12}
              value={quoteText}
              onChange={(e) => setQuoteText(e.target.value)}
              placeholder="Paste every line of the dealer worksheet — MSRP, selling price, monthly, due at signing, money factor / APR, term, mileage, any add-ons (paint protection, etch, nitrogen, etc.)"
              className="w-full bg-[#0A0A0E] border border-[#1A1A24] rounded-xl text-white p-4 text-sm font-mono"
              data-testid="negotiate-text"
            />
          ) : (
            <div>
              <input ref={fileRef} type="file" accept="application/pdf" hidden onChange={(e) => uploadFile(e.target.files?.[0])} data-testid="negotiate-file-input" />
              <button onClick={() => fileRef.current?.click()} className="w-full border border-dashed border-white/15 hover:border-white/30 rounded-xl p-10 flex flex-col items-center gap-3 hover:bg-white/[0.02] transition" data-testid="negotiate-upload-btn">
                <Upload className="w-6 h-6 text-silver" />
                <div className="font-display text-base">{uploading ? "Uploading…" : "Upload dealer quote (PDF)"}</div>
                <div className="text-xs text-gray-500">Max 10MB · we extract text only</div>
              </button>
              {fileName && (
                <div className="mt-4 flex items-center gap-3 p-3 rounded-xl bg-white/5">
                  <FileText className="w-4 h-4 text-silver" />
                  <span className="text-sm">{fileName}</span>
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 ml-auto" />
                </div>
              )}
            </div>
          )}

          <button onClick={analyze} disabled={analyzing} className="mt-5 w-full silver-gradient text-[#050507] font-bold py-3.5 rounded-full disabled:opacity-50" data-testid="negotiate-submit">
            {analyzing ? "Analyzing…" : "Analyze This Deal"}
          </button>
        </div>

        <div className="lg:col-span-5">
          {!result && !analyzing && (
            <div className="glass rounded-2xl p-7 text-sm text-gray-400">
              <p className="font-display text-lg text-white mb-3">What we look for</p>
              <ul className="space-y-2">
                <li>• Selling price vs MSRP</li>
                <li>• Money factor markup</li>
                <li>• Inflated documentation + acquisition fees</li>
                <li>• Dealer add-ons (paint, nitrogen, etch)</li>
                <li>• Term + mileage fit for your usage</li>
              </ul>
            </div>
          )}

          {analyzing && <div className="glass rounded-2xl p-7 text-center"><div className="animate-pulse-slow w-12 h-12 rounded-full silver-gradient mx-auto mb-4" /><div className="label-mono">Analyzing quote</div></div>}

          {result && (
            <div className="glass rounded-2xl p-7 animate-fade-up" data-testid="negotiate-result">
              <div className="text-center mb-7">
                <div className="label-mono mb-2">Fair Deal Score</div>
                <div className={`font-display text-7xl font-light ${scoreColor}`} data-testid="negotiate-score">{result.fair_deal_score}<span className="text-2xl text-gray-500">/10</span></div>
                <p className="text-sm text-gray-300 mt-4 leading-relaxed">{result.verdict}</p>
              </div>

              <div className="border-t border-white/10 pt-5">
                <p className="label-mono mb-3">Extracted Numbers</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {Object.entries(result.extracted || {}).map(([k, v]) => (
                    <div key={k} className="flex justify-between p-2 rounded bg-white/5">
                      <span className="text-gray-500 capitalize">{k.replace(/_/g, " ")}</span>
                      <span>{v ?? "—"}</span>
                    </div>
                  ))}
                </div>
              </div>

              {result.red_flags?.length > 0 && (
                <div className="border-t border-white/10 mt-5 pt-5">
                  <p className="label-mono mb-3 text-red-300 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Red Flags</p>
                  <ul className="space-y-1.5 text-sm">{result.red_flags.map((f, i) => <li key={i} className="text-gray-300">• {f}</li>)}</ul>
                </div>
              )}
              {result.negotiation_tips?.length > 0 && (
                <div className="border-t border-white/10 mt-5 pt-5">
                  <p className="label-mono mb-3">How to Push Back</p>
                  <ul className="space-y-1.5 text-sm">{result.negotiation_tips.map((t, i) => <li key={i} className="text-gray-300">• {t}</li>)}</ul>
                </div>
              )}

              <Link to="/booking" className="mt-6 block text-center silver-gradient text-[#050507] py-3 rounded-full font-bold text-sm">Have a Broker Review This</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
